import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "./AuthContext";
import { Loader2, LogIn, ArrowRight } from "lucide-react";
import { brand } from "@/content/site";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-forest flex items-center justify-center p-5"
    >
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div
          className="absolute -top-32 -right-40 w-[500px] h-[500px] rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, #8DFE3F 0%, transparent 70%)" }}
        />
        <div
          className="absolute -bottom-32 -left-40 w-[500px] h-[500px] rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, #2CD59C 0%, transparent 70%)" }}
        />
      </div>

      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-[2rem] p-8 md:p-10 shadow-[0_40px_80px_rgba(0,0,0,0.25)]">
          <div className="flex justify-center">
            <img src={brand.logo} alt="Green Circle Group" className="h-14 w-auto" />
          </div>
          <p className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-mint text-center">
            Internal CRM
          </p>
          <h1 className="mt-2 text-2xl md:text-3xl font-extrabold text-forest text-center">
            Sign in to Green Circle CRM
          </h1>
          <p className="mt-2 text-sm text-mid-neutral text-center">
            Team access only. Log in with your admin email.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div>
              <label htmlFor="admin-email" className="text-xs font-bold uppercase tracking-[0.15em] text-mid-neutral">
                Email
              </label>
              <input
                id="admin-email"
                type="email"
                data-testid="admin-email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full h-14 px-4 rounded-xl bg-soft border-2 border-transparent focus:border-mint focus:outline-none text-base text-forest"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="text-xs font-bold uppercase tracking-[0.15em] text-mid-neutral">
                Password
              </label>
              <input
                id="admin-password"
                type="password"
                data-testid="admin-password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full h-14 px-4 rounded-xl bg-soft border-2 border-transparent focus:border-mint focus:outline-none text-base text-forest"
              />
            </div>

            {error && (
              <div
                data-testid="admin-login-error"
                role="alert"
                className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="w-full h-14 rounded-xl bg-lime text-forest font-extrabold flex items-center justify-center gap-2 hover:bg-bright-green transition-all disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" /> Signing in…
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5" /> Sign in <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>
        </div>
        <p className="mt-6 text-center text-xs text-white/60">
          Forgot your password? Contact <a href={`mailto:${brand.email}`} className="text-lime underline">{brand.email}</a>
        </p>
      </div>
    </div>
  );
}
